/**
 * Capacity-constrained Portfolio Heatmap (Dashboard Row 1).
 * Bounding square = risk capacity under Settings-defined limits (margin-
 * utilization-implied exposure ceiling — NOT buying power, NOT 100% NLV).
 * Filled tiles = current exposure · dot-grid region = unused risk budget ·
 * dashed blocks = contingent CSP exposure if assigned · inner dashed band =
 * CC callable shares. Hazard stripes = portion over the single-position cap.
 * All numeric/breach detail lives in hover tooltips — no persistent text.
 */
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import api from "@/lib/api";

const heatColor = (pnl) => {
  const x = Math.max(-15, Math.min(15, pnl)) / 15;
  return x >= 0 ? `rgba(16,185,129,${0.12 + 0.5 * x})` : `rgba(239,68,68,${0.12 + 0.5 * -x})`;
};

// Classic squarified treemap: fills `rect` with `items` (needs .value), preserves order.
function squarify(items, rect) {
  const out = [];
  let { x, y, w, h } = rect;
  let total = items.reduce((s, it) => s + it.value, 0);
  let i = 0;
  while (i < items.length && total > 0 && w > 0.5 && h > 0.5) {
    const vertical = w < h;
    const side = vertical ? w : h;
    let row = [], rowVal = 0, bestWorst = Infinity, j = i;
    while (j < items.length) {
      const testVal = rowVal + items[j].value;
      const thick = (testVal / total) * (w * h) / side;
      let worst = 0;
      for (let k = i; k <= j; k++) {
        const len = (items[k].value / testVal) * side;
        worst = Math.max(worst, thick / Math.max(len, 0.01), len / Math.max(thick, 0.01));
      }
      if (worst > bestWorst && row.length) break;
      bestWorst = worst; row.push(items[j]); rowVal = testVal; j++;
    }
    const thick = (rowVal / total) * (w * h) / side;
    let off = 0;
    for (const it of row) {
      const len = (it.value / rowVal) * side;
      out.push(vertical ? { ...it, x: x + off, y, w: len, h: thick }
        : { ...it, x, y: y + off, w: thick, h: len });
      off += len;
    }
    if (vertical) { y += thick; h -= thick; } else { x += thick; w -= thick; }
    total -= rowVal; i = j;
  }
  return out;
}

const fmtK = (v) => Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(v >= 100000 ? 0 : 1)}k` : `$${Math.round(v)}`;

export const CapacityHeatmap = ({ s, positions }) => {
  const [mode, setMode] = useState("heatmap");
  const [labelMode, setLabelMode] = useState("ticker");
  const [earnings, setEarnings] = useState(null);
  const [meta, setMeta] = useState(null); // limits + sectors + contingents
  const [wrapEl, setWrapEl] = useState(null);
  const [dim, setDim] = useState({ w: 0, h: 0 });
  const [tip, setTip] = useState(null); // {x, y, lines: [{text, tone}]}

  useEffect(() => {
    api.get("/dashboard/heatmap-limits").then(({ data }) => setMeta(data))
      .catch((e) => { console.warn("heatmap-limits:", e?.message); setMeta({ limits: {}, sectors: {}, contingents: {} }); });
  }, []);
  useEffect(() => {
    if (mode === "earnings" && earnings === null) {
      api.get("/dashboard/holdings-earnings").then(({ data }) => setEarnings(data.rows || []))
        .catch((e) => { console.warn("holdings-earnings:", e?.message); setEarnings([]); });
    }
  }, [mode, earnings]);
  useEffect(() => {
    if (!wrapEl) return;
    const ro = new ResizeObserver(([e]) => setDim({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(wrapEl);
    return () => ro.disconnect();
  }, [wrapEl]);

  // ── aggregate lots by ticker ──
  const byT = {};
  (positions?.stocks || []).forEach(p => {
    const px = p.last_price || p.avg_cost;
    if (!p.shares || !px) return;
    const t = byT[p.ticker] = byT[p.ticker] || { ticker: p.ticker, value: 0, cost: 0, shares: 0 };
    t.value += p.shares * px;
    t.cost += p.shares * (p.avg_cost || px);
    t.shares += p.shares;
  });
  const items = Object.values(byT)
    .map(t => ({ ...t, pnl_pct: t.cost ? (t.value / t.cost - 1) * 100 : 0 }))
    .sort((a, b) => b.value - a.value);
  const exposure = items.reduce((acc, i) => acc + i.value, 0);
  const nlv = s?.nlv || 0;
  const denom = nlv || exposure;

  // ── capacity ceiling from Settings limits ──
  const lim = meta?.limits || {};
  const capUtil = lim.max_margin_utilization ?? 40;
  const maxSingle = lim.max_single_position_pct ?? 10;
  const maxSector = lim.max_sector_exposure_pct ?? 15;
  const util = s?.margin_used_pct != null ? Math.max(0, s.margin_used_pct)
    : nlv > 0 ? Math.max(0, (s?.maint_margin || 0) / nlv * 100) : 0;
  const cashAccount = util < 1;
  let capacity = cashAccount ? Math.max(nlv, exposure) : exposure * (capUtil / util);
  let truncated = false;
  if (capacity > exposure * 4 && exposure > 0) { capacity = exposure * 4; truncated = true; }
  const overCeiling = exposure > capacity + 1;
  const unusedUsd = Math.max(0, capacity - exposure);

  // ── per-tile & sector breach flags ──
  const singleCapUsd = maxSingle / 100 * denom;
  const sectors = meta?.sectors || {};
  const sectorTotals = {};
  items.forEach(it => {
    const sec = sectors[it.ticker] || "Unknown";
    sectorTotals[sec] = (sectorTotals[sec] || 0) + it.value;
  });
  const breachedSectorPct = Object.fromEntries(Object.entries(sectorTotals)
    .filter(([sec, v]) => sec !== "Unknown" && denom > 0 && v / denom * 100 > maxSector)
    .map(([sec, v]) => [sec, v / denom * 100]));

  // ── contingent CSP blocks (incl. tickers not currently held) ──
  const contingents = meta?.contingents || {};
  const cspBlocks = Object.entries(contingents)
    .filter(([, c]) => c.csp_add_usd > 0)
    .map(([t, c]) => ({ ticker: t, value: c.csp_add_usd, contracts: c.csp_contracts, details: c.csp_details || [] }))
    .sort((a, b) => b.value - a.value);
  const cspTotal = cspBlocks.reduce((a, b) => a + b.value, 0);

  // ── sector-mode aggregation: one tile per sector, flags rolled up ──
  const sectorAgg = {};
  items.forEach(it => {
    const sec = sectors[it.ticker] || "Unknown";
    const g = sectorAgg[sec] = sectorAgg[sec] || { ticker: sec, value: 0, cost: 0, members: [], overUsd: 0, anyContingent: false };
    g.value += it.value; g.cost += it.cost;
    g.members.push(it);
    g.overUsd += Math.max(0, it.value - singleCapUsd);
    const c = contingents[it.ticker];
    if (c && (c.csp_add_usd || c.cc_callable_shares)) g.anyContingent = true;
  });
  const sectorItems = Object.values(sectorAgg)
    .map(g => ({ ...g, pnl_pct: g.cost ? (g.value / g.cost - 1) * 100 : 0 }))
    .sort((a, b) => b.value - a.value);
  const displayItems = labelMode === "sector" ? sectorItems : items;

  const cspBySector = {};
  cspBlocks.forEach(b => {
    const sec = sectors[b.ticker] || "Unknown";
    const g = cspBySector[sec] = cspBySector[sec] || { ticker: sec, value: 0, contracts: 0, members: [] };
    g.value += b.value; g.contracts += b.contracts; g.members.push(b);
  });
  const cspDisplay = labelMode === "sector"
    ? Object.values(cspBySector).sort((a, b) => b.value - a.value) : cspBlocks;

  // ── geometry: capacity square sliced used | contingent+unused along the width.
  // The square scales to max(capacity, exposure + contingents) so CSP blocks
  // can never silently vanish when the book is at/over its ceiling (Bug-1 fix).
  const W = dim.w, H = dim.h;
  const totalVal = Math.max(capacity, exposure + cspTotal, 1);
  const usedFrac = Math.min(1, exposure / totalVal);
  const usedW = W * usedFrac;
  const tileRects = W > 0 && H > 0 ? squarify(displayItems, { x: 0, y: 0, w: usedW, h: H }) : [];
  let cspRects = [];
  if (W > 0 && H > 0 && cspDisplay.length && usedFrac < 1) {
    const regionVal = totalVal - exposure;
    const layoutItems = cspTotal < regionVal
      ? [...cspDisplay, { phantom: true, value: regionVal - cspTotal }] : cspDisplay;
    let cum = 0;
    cspRects = squarify(layoutItems, { x: usedW, y: 0, w: W - usedW, h: H })
      .filter(r => !r.phantom)
      .map(r => { cum += r.value; return { ...r, breach: cum > unusedUsd + 1 }; });
  }
  const ceilingX = totalVal > capacity ? W * (capacity / totalVal) : null;

  // ── hover tooltip helpers ──
  const showTip = (e, lines) => {
    const rect = wrapEl?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, lines });
  };
  const tileTip = (r) => {
    const w = denom ? r.value / denom * 100 : 0;
    if (r.members) {
      const lines = [
        { text: `${r.ticker} — $${Math.round(r.value).toLocaleString()} · ${w.toFixed(1)}% of NLV · P&L ${r.pnl_pct >= 0 ? "+" : ""}${r.pnl_pct.toFixed(1)}%`, tone: "var(--hoip-text-primary)" },
        ...r.members.map(m => ({
          text: `${m.ticker} — ${denom ? (m.value / denom * 100).toFixed(1) : "0.0"}% · P&L ${m.pnl_pct >= 0 ? "+" : ""}${m.pnl_pct.toFixed(1)}%`,
          tone: "var(--hoip-text-secondary)",
        })),
      ];
      const overM = r.members.filter(m => m.value > singleCapUsd + 1);
      if (overM.length)
        lines.push({ text: `${overM.map(m => m.ticker).join(", ")} over ${maxSingle}% single-position cap (${fmtK(singleCapUsd)})`, tone: "#EF4444" });
      if (breachedSectorPct[r.ticker] != null)
        lines.push({ text: `${r.ticker} sector ${breachedSectorPct[r.ticker].toFixed(1)}% > ${maxSector}% cap`, tone: "#F59E0B" });
      if (r.anyContingent)
        lines.push({ text: "Contains contingent CSP/CC exposure — dashed flag; per-strike detail in Ticker view", tone: "#10B981" });
      return lines;
    }
    const sec = sectors[r.ticker] || "Unknown";
    const cc = contingents[r.ticker];
    const lines = [
      { text: `${r.ticker} — $${Math.round(r.value).toLocaleString()} · ${w.toFixed(1)}% of NLV · P&L ${r.pnl_pct >= 0 ? "+" : ""}${r.pnl_pct.toFixed(1)}%`, tone: "var(--hoip-text-primary)" },
    ];
    if (r.value > singleCapUsd + 1)
      lines.push({ text: `Over ${maxSingle}% single-position cap (${fmtK(singleCapUsd)}) by ${fmtK(r.value - singleCapUsd)}`, tone: "#EF4444" });
    if (breachedSectorPct[sec] != null)
      lines.push({ text: `${sec} sector ${breachedSectorPct[sec].toFixed(1)}% > ${maxSector}% cap`, tone: "#F59E0B" });
    if (cc?.cc_callable_shares && r.shares > 0)
      lines.push({ text: `${Math.min(cc.cc_callable_shares, r.shares).toLocaleString()} sh callable via covered calls${cc.cc_strike ? ` @ $${cc.cc_strike}` : ""} — assignment reduces exposure`, tone: "#F59E0B" });
    return lines;
  };
  const cspTip = (r) => r.members ? [
    { text: `${r.ticker} — short puts · ${r.contracts} contract${r.contracts === 1 ? "" : "s"} · +$${r.value.toLocaleString()} if assigned`, tone: "var(--hoip-text-primary)" },
    ...r.members.map(m => ({ text: `${m.ticker} — ${m.details.length ? m.details.join(", ") : `${m.contracts} contracts`} · +$${m.value.toLocaleString()}`, tone: "var(--hoip-text-secondary)" })),
    r.breach
      ? { text: "Would EXCEED remaining risk capacity", tone: "#EF4444" }
      : { text: "Fits within remaining risk capacity", tone: "#10B981" },
  ] : ([
    { text: `${r.ticker} — short put${r.details.length ? ` ${r.details.join(", ")}` : ""} · ${r.contracts} contract${r.contracts === 1 ? "" : "s"}`, tone: "var(--hoip-text-primary)" },
    { text: `+$${r.value.toLocaleString()} share exposure if assigned`, tone: "var(--hoip-text-secondary)" },
    r.breach
      ? { text: "Would EXCEED remaining risk capacity", tone: "#EF4444" }
      : { text: "Fits within remaining risk capacity", tone: "#10B981" },
  ]);

  return (
    <div className="border p-2.5 flex-1 flex flex-col min-h-0" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="heatmap-panel">
      <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--hoip-text-secondary)" }}>
            {mode === "heatmap" ? "Portfolio Heatmap — Risk Capacity" : "Next Earnings"}
          </span>
          {mode === "heatmap" && overCeiling && (
            <span className="text-[8px] px-1.5 py-0.5 rounded-sm font-bold font-mono-data"
              style={{ color: "#EF4444", background: "rgba(239,68,68,0.10)" }}
              title={`Current exposure ${fmtK(exposure)} exceeds your exposure ceiling ${fmtK(capacity)} (margin-util cap ${capUtil}%)`}
              data-testid="heatmap-over-ceiling-badge">OVER CEILING</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {mode === "heatmap" && (
            <div className="flex rounded-sm border overflow-hidden" style={{ borderColor: "var(--hoip-border)" }} data-testid="heatmap-label-toggle">
              {[["ticker", "Ticker"], ["sector", "Sector"]].map(([v, l]) => (
                <button key={v} onClick={() => setLabelMode(v)} className="text-[8px] px-1.5 py-0.5 font-bold transition-colors"
                  style={{ background: labelMode === v ? "rgba(16,185,129,0.12)" : "transparent", color: labelMode === v ? "#10B981" : "var(--hoip-text-tertiary)" }}
                  data-testid={`heatmap-label-toggle-${v}`}>{l}</button>
              ))}
            </div>
          )}
          <div className="flex rounded-sm border overflow-hidden" style={{ borderColor: "var(--hoip-border)" }} data-testid="heatmap-mode-toggle">
            {[["heatmap", "Heatmap"], ["earnings", "Earnings"]].map(([v, l]) => (
              <button key={v} onClick={() => setMode(v)} className="text-[8px] px-1.5 py-0.5 font-bold transition-colors"
                style={{ background: mode === v ? "rgba(59,130,246,0.12)" : "transparent", color: mode === v ? "#3B82F6" : "var(--hoip-text-tertiary)" }}
                data-testid={`heatmap-mode-toggle-${v}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      {mode === "heatmap" ? (
        !positions || !meta ? (
          <div className="flex-1 flex items-center justify-center min-h-[150px]"><RefreshCw className="w-3.5 h-3.5 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} /></div>
        ) : items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>No stock positions.</div>
        ) : (
          <div ref={setWrapEl} className="flex-1 min-h-[150px] relative" data-testid="heatmap-tiles" onMouseLeave={() => setTip(null)}>
            {W > 4 && H > 4 && (
              <svg width={W} height={H} style={{ position: "absolute", inset: 0 }}>
                <defs>
                  <pattern id="hz-red" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45)">
                    <rect width="7" height="7" fill="rgba(239,68,68,0.10)" />
                    <line x1="0" y1="0" x2="0" y2="7" stroke="rgba(239,68,68,0.55)" strokeWidth="2.5" />
                  </pattern>
                  <pattern id="cap-dots" patternUnits="userSpaceOnUse" width="10" height="10">
                    <circle cx="2" cy="2" r="0.8" fill="var(--hoip-border)" />
                  </pattern>
                </defs>
                {/* capacity square background = unused risk budget */}
                <rect x="0" y="0" width={W} height={H} rx="2" fill="var(--hoip-bg)" />
                <rect x="0" y="0" width={W} height={H} rx="2" fill="url(#cap-dots)" />
                <rect x="0" y="0" width={W} height={H} rx="2" fill="none"
                  stroke={overCeiling ? "#EF4444" : "var(--hoip-border)"} strokeWidth={overCeiling ? 2 : 1}
                  strokeDasharray={overCeiling ? "6 3" : undefined} />
                {usedFrac < 1 && (W - usedW) > 46 && unusedUsd > 0 && (
                  <text x={W - 5} y={H - 6} textAnchor="end" fontSize="8.5" fontFamily="'JetBrains Mono', monospace"
                    fill="var(--hoip-text-tertiary)" data-testid="unused-capacity-label">
                    unused {fmtK(unusedUsd)}
                  </text>
                )}
                {ceilingX != null && ceilingX > 8 && (
                  <g data-testid="capacity-ceiling-marker">
                    <line x1={ceilingX} y1={0} x2={ceilingX} y2={H} stroke="#EF4444" strokeWidth="1.4" strokeDasharray="4 3" />
                    <text x={ceilingX + 4} y={11} fontSize="8" fontFamily="'JetBrains Mono', monospace" fill="#EF4444">ceiling</text>
                  </g>
                )}
                {/* position tiles */}
                {tileRects.map(r => {
                  const isSec = !!r.members;
                  const sec = isSec ? r.ticker : (sectors[r.ticker] || "Unknown");
                  const secBreach = breachedSectorPct[sec] != null;
                  const overFrac = isSec
                    ? (r.overUsd > 0 ? Math.min(1, r.overUsd / r.value) : 0)
                    : (r.value > singleCapUsd ? Math.min(1, (r.value - singleCapUsd) / r.value) : 0);
                  const w = denom ? r.value / denom * 100 : 0;
                  const cc = isSec ? null : contingents[r.ticker];
                  const callableFrac = cc?.cc_callable_shares && r.shares > 0
                    ? Math.min(1, cc.cc_callable_shares / r.shares) : 0;
                  const showTicker = r.w > 30 && r.h > 16;
                  const showSub = r.w > 44 && r.h > 42;
                  const label = r.ticker;
                  const labelFs = isSec
                    ? Math.min(10.5, Math.max(6.5, (r.w - 8) / (label.length * 0.62)))
                    : Math.min(12, Math.max(9, r.w / 6));
                  const cx = r.x + r.w / 2;
                  return (
                    <g key={r.ticker} data-testid={`heatmap-tile-${r.ticker}`}
                      onMouseMove={(e) => showTip(e, tileTip(r))} onMouseLeave={() => setTip(null)}>
                      <rect x={r.x} y={r.y} width={r.w} height={r.h} rx={2}
                        fill={heatColor(r.pnl_pct)} stroke="var(--hoip-card)" strokeWidth="2" />
                      {overFrac > 0 && (
                        <rect x={r.x + r.w * (1 - overFrac)} y={r.y} width={r.w * overFrac} height={r.h} rx={2}
                          fill="url(#hz-red)" stroke="#EF4444" strokeWidth="1"
                          data-testid={`heatmap-overcap-${r.ticker}`} />
                      )}
                      {secBreach && (
                        <rect x={r.x + 1} y={r.y + 1} width={Math.max(0, r.w - 2)} height={Math.max(0, r.h - 2)} rx={2}
                          fill="none" stroke="#F59E0B" strokeWidth="1.2" strokeDasharray="3 2"
                          data-testid={`heatmap-sector-breach-${r.ticker}`} />
                      )}
                      {isSec && r.anyContingent && (
                        <rect x={r.x + 3.5} y={r.y + 3.5} width={Math.max(0, r.w - 7)} height={Math.max(0, r.h - 7)} rx={2}
                          fill="none" stroke="#10B981" strokeWidth="1" strokeDasharray="4 3"
                          data-testid={`heatmap-sector-contingent-${sec}`} />
                      )}
                      {callableFrac > 0 && r.h > 24 && (
                        <rect x={r.x + 3} y={r.y + r.h - Math.max(6, r.h * callableFrac) - 3}
                          width={Math.max(0, r.w - 6)} height={Math.max(6, r.h * callableFrac)} rx={1.5}
                          fill="rgba(245,158,11,0.06)" stroke="#F59E0B" strokeWidth="1" strokeDasharray="4 3"
                          data-testid={`heatmap-callable-${r.ticker}`} />
                      )}
                      {showTicker && (
                        <text x={cx} y={r.y + r.h / 2 + (showSub ? -8 : 3.5)} textAnchor="middle" fontWeight="700"
                          fontSize={labelFs} fontFamily="'JetBrains Mono', monospace"
                          fill="var(--hoip-text-primary)" style={{ pointerEvents: "none" }}>{label}</text>
                      )}
                      {showSub && (
                        <>
                          <text x={cx} y={r.y + r.h / 2 + 4} textAnchor="middle" fontSize="8.5" fontFamily="'JetBrains Mono', monospace" fill="var(--hoip-text-secondary)" style={{ pointerEvents: "none" }}>{w.toFixed(1)}%</text>
                          <text x={cx} y={r.y + r.h / 2 + 14} textAnchor="middle" fontSize="8.5" fontFamily="'JetBrains Mono', monospace" fill={r.pnl_pct >= 0 ? "#34D399" : "#F87171"} style={{ pointerEvents: "none" }}>{r.pnl_pct >= 0 ? "+" : ""}{r.pnl_pct.toFixed(1)}%</text>
                        </>
                      )}
                    </g>
                  );
                })}
                {/* contingent CSP exposure — dashed blocks in the unused-budget region */}
                {cspRects.map(r => {
                  const col = r.breach ? "#EF4444" : "#10B981";
                  const showLbl = r.w > 22 && r.h > 12;
                  const showVal = r.w > 30 && r.h > 26;
                  const cspLabel = r.ticker;
                  const cspFs = labelMode === "sector"
                    ? Math.min(9, Math.max(6, (r.w - 8) / (cspLabel.length * 0.62)))
                    : Math.min(9, Math.max(5.5, r.w / 7));
                  return (
                    <g key={`csp-${r.ticker}`} data-testid={`heatmap-csp-${r.ticker}`}
                      onMouseMove={(e) => showTip(e, cspTip(r))} onMouseLeave={() => setTip(null)}>
                      <rect x={r.x + 2} y={r.y + 2} width={Math.max(0, r.w - 4)} height={Math.max(0, r.h - 4)} rx={2}
                        fill={r.breach ? "rgba(239,68,68,0.05)" : "rgba(16,185,129,0.04)"}
                        stroke={col} strokeWidth="1.2" strokeDasharray="5 3" />
                      {showLbl && (
                        <>
                          <text x={r.x + r.w / 2} y={r.y + r.h / 2 + (showVal ? -1 : 2.5)} textAnchor="middle" fontSize={cspFs} fontWeight="700"
                            fontFamily="'JetBrains Mono', monospace" fill={col} style={{ pointerEvents: "none" }}>{cspLabel}</text>
                          {showVal && (
                            <text x={r.x + r.w / 2} y={r.y + r.h / 2 + 9} textAnchor="middle" fontSize="7.5"
                              fontFamily="'JetBrains Mono', monospace" fill="var(--hoip-text-tertiary)" style={{ pointerEvents: "none" }}>+{fmtK(r.value)}</text>
                          )}
                        </>
                      )}
                    </g>
                  );
                })}
              </svg>
            )}
            {/* custom hover tooltip */}
            {tip && (
              <div className="absolute z-10 pointer-events-none border rounded-sm px-2 py-1.5 space-y-0.5"
                style={{
                  left: Math.min(tip.x + 12, Math.max(0, W - 240)), top: tip.y + 14 > H - 40 ? tip.y - 48 : tip.y + 14,
                  background: "var(--hoip-card)", borderColor: "var(--hoip-border)", maxWidth: 236,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                }} data-testid="heatmap-tooltip">
                {tip.lines.map((l, i) => (
                  <div key={i} className="text-[9px] leading-snug font-mono-data" style={{ color: l.tone }}>{l.text}</div>
                ))}
              </div>
            )}
          </div>
        )
      ) : (
        <div className="flex-1 overflow-y-auto min-h-[96px] max-h-[210px]" data-testid="earnings-table-wrap">
          {earnings === null ? (
            <div className="flex items-center justify-center h-full"><RefreshCw className="w-3 h-3 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} /></div>
          ) : (
            <table className="w-full dense-table text-[9px]" data-testid="earnings-table">
              <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                <th className="text-left font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
                <th className="text-left font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Next Earnings</th>
                <th className="text-right font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Days</th>
              </tr></thead>
              <tbody>
                {earnings.map(r => (
                  <tr key={r.ticker} className="border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                    <td className="font-mono-data font-bold">{r.ticker}</td>
                    <td className="font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{r.date || "—"}{r.date && !r.confirmed ? <span className="text-[7px] ml-1" style={{ color: "var(--hoip-text-tertiary)" }}>est.</span> : null}</td>
                    <td className={`text-right font-mono-data font-medium ${r.days_until != null && r.days_until <= 7 ? "text-amber-400" : ""}`}>{r.days_until ?? "—"}</td>
                  </tr>
                ))}
                {earnings.length === 0 && <tr><td colSpan={3} className="text-center py-3" style={{ color: "var(--hoip-text-tertiary)" }}>No holdings.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      )}
      <p className="text-[7px] italic mt-1 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>
        {mode === "heatmap"
          ? `Square = exposure ceiling under your Settings limits (${cashAccount ? "cash account: ceiling = NLV" : `margin-util cap ${capUtil}%`}${truncated ? ", display truncated at 4× exposure" : ""}) · stripes = over single-position cap · dashed = contingent CSP/CC · hover for detail. Mechanical restatement of your own limits — not advice.`
          : "Dates from the earnings cache; 'est.' = derived from scanner days-to-earnings."}
      </p>
    </div>
  );
};
